import React, { useEffect, useRef, useState } from "react";
import { Form, Input, Button, Typography, ConfigProvider, App } from "antd";
import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import { takeTerminalAuthReason } from "../../utils/terminalAuthError";
import Icon from "@ant-design/icons";
import { GoogleSvg } from "../../assets/icons";
import LoginImage from "../../assets/login_image.webp";
import { loginWithGoogle, loginWithEmail } from "../../slices/authSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import logger from "../../utils/logger";

// Lookup of supported `?info=...` banner messages, shown to a user who arrived
// here from another auth-flow page that flagged why (an already-activated
// invitation re-click, an inactivity sign-out, a session that ended). Keep the
// map small and purposeful, and extend it only when a new flow needs a
// similarly framed message.
const LOGIN_INFO_BANNERS = {
  already_activated: "Your account is already active. Please log in below.",
  session_expired: "You've been signed out due to inactivity. Please sign in again.",
  session_ended: "Your session has ended. Please sign in again to continue.",
};


const GoogleIcon = (props) => (
  <Icon
    component={GoogleSvg}
    {...props}
  />
);

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  // A successful login sets isAuthenticated (userData is fetched later by
  // ProtectedRoute, which isn't mounted here), so we gate the declarative
  // redirect on isAuthenticated — not userData, which is always null on /login.
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);

  // Banner shown when the user lands here from a redirect that flagged a
  // user-meaningful state (e.g. already-activated re-click of an old
  // invitation link). The text is captured once at mount and the `?info`
  // param is stripped from the URL so refresh doesn't keep showing it.
  const [infoBanner, _setInfoBanner] = useState(() => {
    const code = searchParams.get("info");
    return code ? LOGIN_INFO_BANNERS[code] || null : null;
  });

  useEffect(() => {
    if (!searchParams.get("info")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("info");
    setSearchParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Why the session ended, when the server refused this identity outright (a
  // deactivated account) and the axios interceptor signed the user out. Without
  // this the user is returned to a blank login screen and re-enters correct
  // credentials that cannot work — the server's explanation is the only one
  // available, and it is gone by the time this screen mounts.
  //
  // Held in a ref rather than read in a `useState` initializer: the take clears
  // the stored reason, and StrictMode double-invokes initializers in
  // development, so the second call would read null and the message could be
  // lost in dev but not in production. A ref is stable across that double
  // render, so the take happens exactly once per mount. A later, unrelated
  // visit mounts fresh, takes null, and correctly shows nothing.
  const terminalReasonRef = useRef(undefined);
  if (terminalReasonRef.current === undefined) {
    terminalReasonRef.current = takeTerminalAuthReason();
  }
  const terminalReason = terminalReasonRef.current;

  // React Router stashes the pre-login URL in location.state.from when
  // ProtectedRoute redirects here. Return the user there after sign-in so
  // deep links (e.g. Slack-shared /chat/:chatId/:canvasId) survive the login step.
  const postLoginDestination = () => {
    const from = location.state?.from;
    if (!from) return "/";
    return `${from.pathname || "/"}${from.search || ""}${from.hash || ""}`;
  };

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const email = import.meta.env.VITE_DEV_EMAIL;
    const password = import.meta.env.VITE_DEV_PASSWORD;
    if (!email || !password) return;
    // Attempt dev auto-login at most once per browser tab session. Without this guard
    // every LoginPage mount — page reload, redirect bounce, or React StrictMode's
    // double-mount — fires another Firebase sign-in, and a *failed* attempt retries on
    // the next mount. That loop hammers Firebase Auth and trips rate limits
    // (auth/too-many-requests) for the whole team. The flag is set *before* the request
    // so an in-flight attempt can't be duplicated; open a new tab (or clear
    // sessionStorage) to retry after fixing creds / waiting out a rate limit.
    if (sessionStorage.getItem("devAutoLoginAttempted") === "1") return;
    sessionStorage.setItem("devAutoLoginAttempted", "1");
    dispatch(loginWithEmail({ email, password, message }))
      .unwrap()
      .then(() => navigate(postLoginDestination()))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFinish = async (values) => {
    message.loading({ key: "auth", content: "Login in progress...", duration: 0 });
    try {
      await dispatch(
        loginWithEmail({
          email: values.email,
          password: values.password,
          message,
        })
      ).unwrap();

      navigate(postLoginDestination());
    } catch (error) {
      logger.error("Email login error:", error);

      message.error({
        key: "auth",
        content: "Login failed! Please check your credentials.",
        duration: 3,
      });
    }
  };

  const onFinishFailed = (errorInfo) => {
    logger.error("Failed:", errorInfo);
    message.error({
      key: "auth",
      content: "Please fill in all required fields correctly.",
      duration: 3,
    });
  };

  const handleGoogleLogin = async () => {
    message.loading({ key: "auth", content: "Login in progress...", duration: 0 });
    try {
      await dispatch(loginWithGoogle({ message })).unwrap();

      navigate(postLoginDestination());
    } catch (error) {
      logger.error("Error storing data", error);

      // Handle QuotaExceededError specifically
      if (error?.name === "QuotaExceededError" || error?.message?.includes("quota")) {
        console.error("❌ Storage quota exceeded - clearing localStorage...");
        try {
          localStorage.clear();
          message.warning({
            key: "auth",
            content: "Storage cleared due to quota limit. Please login again.",
            duration: 5,
          });
          window.location.reload();
        } catch (clearError) {
          console.error("Could not clear localStorage:", clearError);
        }
        return;
      }

      // Handle cancelled popup
      if (error?.message?.includes("cancelled") || error?.code === "auth/cancelled-popup-request") {
        // User cancelled the popup, no need to show error
        return;
      }

      message.error({
        key: "auth",
        content: "Login failed!",
        duration: 3,
      });
    }
  };

  // Declarative redirect: once auth is true, leave /login even if the imperative
  // navigate in the submit handlers raced ahead of the auth-state propagation and
  // ProtectedRoute bounced back here. It re-fires when state settles. A failed
  // profile fetch is ProtectedRoute's to show, so it does not hold the user here.
  if (isAuthenticated) {
    return <Navigate to={postLoginDestination()} replace />;
  }

  return (
    <div className="flex justify-center items-center h-[100vh] w-[100vw] ">
      <div className="flex justify-center items-center flex-1 flex-col gap-[2rem]">
        <div className="absolute top-[1.625rem] left-[1.625rem] text-[black] text-[1rem] font-[400] tracking-[1.2px]">
          Dash
          <span className="text-[1.25rem] font-[600] tracking-normal">ONE</span>
        </div>
        <div className="w-full max-w-[400px] flex flex-col">
          <div className="flex flex-col gap-[0.75rem] text-left mb-[2rem]">
            <div className="text-[1.875rem] font-[600] text-[rgb(16,24,40)]">
              Welcome Back
            </div>
            <div className="text-[1rem] font-[400] text-[rgb(71,84,103)]">
              Welcome back! Please Enter your details.
            </div>
          </div>
          {/* Persistent, not a toast: this explains why the user was signed
              out, and a message that disappears on its own is one they can
              miss entirely while reading the form. `role="alert"` (not
              "status") so a screen reader announces it immediately — the user
              is about to retype credentials that cannot work. */}
          {terminalReason && (
            <div
              role="alert"
              className="bg-[#fff2f0] border border-[#ff4d4f] rounded-[24px] px-[18px] py-[14px] mb-[1.5rem] flex items-start gap-[10px]"
            >
              <ExclamationCircleFilled className="text-[#ff4d4f] text-[18px] mt-[2px]" />
              <span className="text-[14px] leading-[22px] text-[rgba(0,0,0,0.85)]">
                {terminalReason}
              </span>
            </div>
          )}
          {infoBanner && (
            <div
              role="status"
              className="bg-[#f6ffed] border border-[#389e0d] rounded-[24px] px-[18px] py-[14px] mb-[1.5rem] flex items-start gap-[10px]"
            >
              <CheckCircleFilled className="text-[#389e0d] text-[18px] mt-[2px]" />
              <span className="text-[14px] leading-[22px] text-[rgba(0,0,0,0.85)]">
                {infoBanner}
              </span>
            </div>
          )}
          <ConfigProvider theme={{ token: { colorPrimary: "#0E49B5" } }}>
            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              layout="vertical"
              className="w-full"
            >
              {/* Email Field */}
              <Form.Item
                label={
                  <span className="text-[0.875rem] font-[500] text-[#344054] font-[Inter]">
                    Email
                  </span>
                }
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please input your email!",
                  },
                  {
                    type: "email",
                    message: "Please enter a valid email!",
                  },
                ]}
                className="mb-[1rem]"
              >
                <Input
                  placeholder="Enter your email"
                  size="large"
                  className="rounded-[8px] border-[#D0D5DD] py-[12px] px-[14px]"
                  style={{
                    fontSize: "16px",
                    color: "black",
                    fontFamily: "Inter",
                  }}
                />
              </Form.Item>

              {/* Password Field */}
              <Form.Item
                label={
                  <span className="text-[0.875rem] font-[500] text-[rgb(52,64,84)] font-[Inter]">
                    Password
                  </span>
                }
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Please input your password!",
                  },
                ]}
                className="mb-[1rem]"
              >
                <Input.Password
                  placeholder="••••••••"
                  size="large"
                  className="rounded-[8px] border-[#D0D5DD] py-[12px] px-[14px]"
                  style={{
                    fontSize: "16px",
                    fontFamily: "Inter",
                  }}
                />
              </Form.Item>

              {/* Forgot password */}
              <div className="flex justify-end items-center mb-[1.5rem]">
                <Button
                  type="link"
                  onClick={() => navigate("/forgot-password")}
                  className="p-0 text-[#0E49B5] text-[0.875rem] font-[500] hover:text-[#0E49B5]/80"
                  style={{ fontFamily: "Inter" }}
                >
                  Forgot password
                </Button>
              </div>

              {/* Sign in Button */}
              <Form.Item className="mb-[1rem]">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="w-full border-none rounded-[8px] h-[48px] text-[1rem] font-[600]"
                  style={{ fontFamily: "Inter" }}
                >
                  Sign in
                </Button>
              </Form.Item>

              {/* Google Sign in Button */}
              <Form.Item className="mb-[1.5rem]">
                <Button
                  onClick={handleGoogleLogin}
                  size="large"
                  className="w-full"
                  style={{ fontFamily: "Inter" }}
                >
                  <GoogleIcon className="text-[1.25rem]" />
                  Sign in with Google
                </Button>
              </Form.Item>

              {/* Sign up link */}
              {/* <div className="text-center">
                <Text className="text-[0.875rem] text-[rgb(71,84,103)]">
                  Don't have an account?{" "}
                  <Button
                    color="primary"
                    variant="link"
                    style={{ padding: 0 }}
                  >
                    Sign up
                  </Button>
                </Text>
              </div> */}
            </Form>
          </ConfigProvider>
        </div>
      </div>
      <div className="absolute bottom-[2rem] left-[1.625rem] text-[rgb(71,84,103)] text-[0.75rem] font-[400] ">
        @DashONE 2025
      </div>
      <div
        className="flex-1 h-full w-[50vw] self-stretch rounded-[20px_0_0_20px] bg-no-repeat bg-center bg-cover "
        style={{ backgroundImage: `url(${LoginImage})` }}
      />
    </div>
  );
};

export default LoginPage;
